import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../../components/feature/AdminLayout';
import {
  getAllModerationReviews,
  approveReview,
  rejectReview,
  deleteReviewFromModeration,
  type ModerationReview,
  type ReviewStatus,
} from '../../../utils/reviewStorage';

type FilterTab = 'pending' | 'approved' | 'rejected';

const tabConfig: Record<FilterTab, { label: string; icon: string; color: string; empty: string }> = {
  pending:  { label: 'Pending',  icon: 'ri-time-line',            color: 'text-amber-600 bg-amber-50 border-amber-200',  empty: 'No reviews awaiting moderation.' },
  approved: { label: 'Approved', icon: 'ri-checkbox-circle-line', color: 'text-green-600 bg-green-50 border-green-200',  empty: 'No approved reviews yet.'        },
  rejected: { label: 'Rejected', icon: 'ri-close-circle-line',    color: 'text-red-500 bg-red-50 border-red-200',        empty: 'No rejected reviews.'            },
};

function StarRow({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className={`w-3.5 h-3.5 flex items-center justify-center ${i < rating ? 'text-amber-400' : 'text-stone-200'}`}>
          <i className="ri-star-fill text-xs" />
        </div>
      ))}
    </div>
  );
}

interface ReviewRowProps {
  review: ModerationReview;
  onApprove: (id: string) => void;
  onReject:  (id: string) => void;
  onDelete:  (id: string) => void;
}

function ReviewRow({ review, onApprove, onReject, onDelete }: ReviewRowProps) {
  const [expanded, setExpanded] = useState(false);
  const submitted = new Date(review.submittedAt);
  const dateStr = submitted.toLocaleDateString('en-GY', { day: 'numeric', month: 'short', year: 'numeric' });
  const timeStr = submitted.toLocaleTimeString('en-GY', { hour: '2-digit', minute: '2-digit' });

  const statusCfg = tabConfig[review.status as FilterTab];

  return (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
      {/* Row header */}
      <div
        className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-stone-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-orange-400 to-orange-600">
          {review.avatar ? (
            <img src={review.avatar} alt={review.userName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white font-bold text-sm">
              {review.userName.charAt(0)}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-stone-900">{review.userName}</p>
            <StarRow rating={review.rating} />
          </div>
          <p className="text-xs text-stone-500 truncate mt-0.5">
            <span className="font-medium text-stone-700">{review.beverageName}</span>
            {review.title && <> &middot; &quot;{review.title}&quot;</>}
          </p>
        </div>

        {/* Status + date */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className={`hidden sm:inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${statusCfg.color}`}>
            <i className={`${statusCfg.icon} text-xs`} />
            {statusCfg.label}
          </span>
          <div className="text-right">
            <p className="text-xs font-medium text-stone-600">{dateStr}</p>
            <p className="text-xs text-stone-400">{timeStr}</p>
          </div>
          <div className="w-5 h-5 flex items-center justify-center text-stone-400">
            <i className={`text-base ${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
          </div>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-stone-100 px-5 py-4 bg-stone-50/50">
          {/* Review text */}
          <div className="bg-white border border-stone-100 rounded-xl p-4 mb-4">
            {review.title && (
              <p className="text-sm font-bold text-stone-900 mb-1.5">&quot;{review.title}&quot;</p>
            )}
            <p className="text-sm text-stone-600 leading-relaxed">{review.comment}</p>
          </div>

          {/* Meta */}
          <div className="flex items-center gap-4 text-xs text-stone-400 mb-4 flex-wrap">
            <span className="flex items-center gap-1">
              <i className="ri-goblet-line" /> {review.beverageName}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" /> Submitted {dateStr} at {timeStr}
            </span>
            {review.verified && (
              <span className="flex items-center gap-1 text-green-600 font-medium">
                <i className="ri-verified-badge-fill" /> Verified purchase
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {review.status === 'pending' && (
              <>
                <button
                  onClick={() => onApprove(review.id)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-xs font-bold rounded-lg transition cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-checkbox-circle-line text-sm" />
                  Approve
                </button>
                <button
                  onClick={() => onReject(review.id)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-lg transition cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-circle-line text-sm" />
                  Reject
                </button>
              </>
            )}
            {review.status === 'rejected' && (
              <button
                onClick={() => onApprove(review.id)}
                className="flex items-center gap-1.5 px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-xs font-bold rounded-lg transition cursor-pointer whitespace-nowrap"
              >
                <i className="ri-checkbox-circle-line text-sm" />
                Approve anyway
              </button>
            )}
            {review.status === 'approved' && (
              <button
                onClick={() => onReject(review.id)}
                className="flex items-center gap-1.5 px-4 py-2 border border-red-200 text-red-500 hover:bg-red-50 text-xs font-semibold rounded-lg transition cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-circle-line text-sm" />
                Revoke approval
              </button>
            )}
            <button
              onClick={() => onDelete(review.id)}
              className="flex items-center gap-1.5 px-4 py-2 border border-stone-200 text-stone-500 hover:bg-stone-100 text-xs font-semibold rounded-lg transition cursor-pointer whitespace-nowrap ml-auto"
            >
              <i className="ri-delete-bin-2-line text-sm" />
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminReviewsPage() {
  const [tab, setTab] = useState<FilterTab>('pending');
  const [reviews, setReviews] = useState<ModerationReview[]>([]);

  const loadReviews = useCallback(() => {
    setReviews(getAllModerationReviews());
  }, []);

  useEffect(() => {
    loadReviews();
    const interval = setInterval(loadReviews, 4000);
    return () => clearInterval(interval);
  }, [loadReviews]);

  const handleApprove = (id: string) => { approveReview(id); loadReviews(); };
  const handleReject  = (id: string) => { rejectReview(id);  loadReviews(); };
  const handleDelete  = (id: string) => { deleteReviewFromModeration(id); loadReviews(); };

  const counts: Record<ReviewStatus, number> = { pending: 0, approved: 0, rejected: 0 };
  reviews.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });

  const filtered = reviews.filter((r) => r.status === tab);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Review Moderation</h1>
          <p className="text-sm text-stone-500 mt-0.5">Approve or reject customer reviews before they go public</p>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-3 gap-4">
          {(Object.entries(tabConfig) as [FilterTab, typeof tabConfig[FilterTab]][]).map(([key, cfg]) => (
            <div key={key} className="bg-white border border-stone-200 rounded-xl p-4 flex items-center gap-4">
              <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${cfg.color}`}>
                <i className={`${cfg.icon} text-base`} />
              </div>
              <div>
                <p className="text-xl font-bold text-stone-900">{counts[key]}</p>
                <p className="text-xs text-stone-500">{cfg.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Pending alert */}
        {counts.pending > 0 && tab !== 'pending' && (
          <div
            className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 cursor-pointer"
            onClick={() => setTab('pending')}
          >
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
            <p className="text-sm font-semibold text-amber-800">
              {counts.pending} review{counts.pending > 1 ? 's' : ''} waiting for your decision
            </p>
            <span className="ml-auto text-xs text-amber-600 font-medium whitespace-nowrap">View &rarr;</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-stone-100 rounded-xl p-1 w-fit">
          {(Object.entries(tabConfig) as [FilterTab, typeof tabConfig[FilterTab]][]).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                tab === key ? 'bg-white text-stone-900' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              <i className={`${cfg.icon} text-sm`} />
              {cfg.label}
              {counts[key] > 0 && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                  tab === key ? 'bg-stone-100 text-stone-700' : 'bg-stone-200 text-stone-500'
                }`}>
                  {counts[key]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Reviews list */}
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white border border-stone-200 rounded-2xl">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-stone-100 text-stone-400 mb-4">
              <i className="ri-chat-check-line text-2xl" />
            </div>
            <p className="text-stone-600 font-medium">{tabConfig[tab].empty}</p>
            <p className="text-xs text-stone-400 mt-1">
              {tab === 'pending' ? 'New reviews will appear here automatically.' : 'Nothing to show right now.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((review) => (
              <ReviewRow
                key={review.id}
                review={review}
                onApprove={handleApprove}
                onReject={handleReject}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
